
import { AuditEntry, AuditStats, DataAccessEvent } from '../types/audit';

class AuditLogger {
  private auditLog: AuditEntry[] = [];
  private maxLogSize = 10000; // Mantener solo las últimas 10k entradas

  // 🔒 CORE AUDIT FUNCTION - Log data access events
  logDataAccess(event: DataAccessEvent): void {
    const entry: AuditEntry = {
      id: crypto.randomUUID(),
      timestamp: new Date(),
      action: event.action,
      userId: event.userId,
      resourceType: event.resourceType,
      resourceId: event.resourceId,
      details: {
        sensitiveDataAccessed: event.sensitiveDataAccessed,
        userAgent: event.userAgent,
        ipAddress: event.ipAddress,
      },
      securityLevel: event.sensitiveDataAccessed ? 'high' : 'medium',
    };

    this.addEntry(entry);
    
    // Log to console for development/debugging
    console.log('🔍 Data Access Audit:', {
      action: event.action,
      resourceType: event.resourceType,
      userId: event.userId,
      sensitive: event.sensitiveDataAccessed,
      timestamp: entry.timestamp.toISOString(),
    });
  }

  // 🔒 PERFORMANCE MONITORING
  async monitorPerformance<T>(
    operation: () => Promise<T>,
    context: {
      action: string;
      resourceType: string;
      userId?: string;
      resourceId?: string;
    }
  ): Promise<T> {
    const startTime = performance.now();
    const startMemory = (performance as any).memory?.usedJSHeapSize;

    try {
      const result = await operation();
      const duration = performance.now() - startTime;
      
      const entry: AuditEntry = {
        id: crypto.randomUUID(),
        timestamp: new Date(),
        action: context.action,
        userId: context.userId,
        resourceType: context.resourceType,
        resourceId: context.resourceId,
        details: { status: 'success' },
        performanceMetrics: {
          duration,
          memoryUsage: startMemory ? (performance as any).memory?.usedJSHeapSize - startMemory : undefined,
        },
        securityLevel: 'low',
      };

      this.addEntry(entry);
      
      // Alert on slow operations
      if (duration > 2000) {
        console.warn('⚠️ Slow Operation Detected:', {
          action: context.action,
          duration: `${duration.toFixed(2)}ms`,
          resourceType: context.resourceType,
        });
      }

      return result;
    } catch (error) {
      const duration = performance.now() - startTime;
      
      const entry: AuditEntry = {
        id: crypto.randomUUID(),
        timestamp: new Date(),
        action: context.action,
        userId: context.userId,
        resourceType: context.resourceType,
        resourceId: context.resourceId,
        details: { 
          status: 'error',
          error: error instanceof Error ? error.message : 'Unknown error'
        },
        performanceMetrics: { duration },
        securityLevel: 'critical',
      };

      this.addEntry(entry);
      throw error;
    }
  }

  // 🔒 SECURITY MONITORING
  logSecurityEvent(
    action: string,
    details: Record<string, any>,
    securityLevel: AuditEntry['securityLevel'] = 'medium'
  ): void {
    const entry: AuditEntry = {
      id: crypto.randomUUID(),
      timestamp: new Date(),
      action,
      resourceType: 'security',
      details,
      securityLevel,
    };

    this.addEntry(entry);

    if (securityLevel === 'critical' || securityLevel === 'high') {
      console.warn('🚨 Security Alert:', {
        action,
        level: securityLevel,
        details,
        timestamp: entry.timestamp.toISOString(),
      });
    }
  }

  // 🔒 GET AUDIT STATISTICS
  getAuditStats(): AuditStats {
    const now = new Date();
    const last24Hours = new Date(now.getTime() - 24 * 60 * 60 * 1000);
    
    const recentEntries = this.auditLog.filter(entry => entry.timestamp >= last24Hours);
    
    const uniqueUsers = new Set(
      recentEntries
        .filter(entry => entry.userId)
        .map(entry => entry.userId)
    ).size;

    const performanceEntries = recentEntries.filter(entry => entry.performanceMetrics);
    const averageResponseTime = performanceEntries.length > 0
      ? performanceEntries.reduce((sum, entry) => sum + (entry.performanceMetrics?.duration || 0), 0) / performanceEntries.length
      : 0;

    const securityAlerts = recentEntries.filter(
      entry => entry.securityLevel === 'high' || entry.securityLevel === 'critical'
    ).length;

    const actionCounts = recentEntries.reduce((acc, entry) => {
      acc[entry.action] = (acc[entry.action] || 0) + 1;
      return acc;
    }, {} as Record<string, number>);

    const topActions = Object.entries(actionCounts)
      .sort(([, a], [, b]) => b - a)
      .slice(0, 5)
      .map(([action, count]) => ({ action, count }));

    return {
      totalAccesses: recentEntries.length,
      uniqueUsers,
      averageResponseTime,
      securityAlerts,
      lastActivity: recentEntries.length > 0 ? recentEntries[recentEntries.length - 1].timestamp : new Date(),
      topActions,
    };
  }

  // 🔒 GET RECENT AUDIT ENTRIES
  getRecentEntries(limit: number = 100): AuditEntry[] {
    return this.auditLog
      .sort((a, b) => b.timestamp.getTime() - a.timestamp.getTime())
      .slice(0, limit);
  }

  // 🔒 GET SECURITY ALERTS
  getSecurityAlerts(limit: number = 50): AuditEntry[] {
    return this.auditLog
      .filter(entry => entry.securityLevel === 'high' || entry.securityLevel === 'critical')
      .sort((a, b) => b.timestamp.getTime() - a.timestamp.getTime())
      .slice(0, limit);
  }

  private addEntry(entry: AuditEntry): void {
    this.auditLog.push(entry);
    
    // Mantener el tamaño del log bajo control
    if (this.auditLog.length > this.maxLogSize) {
      this.auditLog = this.auditLog.slice(-this.maxLogSize);
    }
  }

  // 🔒 CLEAR OLD ENTRIES (for maintenance)
  clearOldEntries(olderThanDays: number = 30): void {
    const cutoffDate = new Date();
    cutoffDate.setDate(cutoffDate.getDate() - olderThanDays);
    
    const beforeCount = this.auditLog.length;
    this.auditLog = this.auditLog.filter(entry => entry.timestamp >= cutoffDate);
    const afterCount = this.auditLog.length;
    
    console.log(`🧹 Audit Cleanup: Removed ${beforeCount - afterCount} old entries`);
  }
}

// 🔒 SINGLETON INSTANCE
export const auditLogger = new AuditLogger();

// 🔒 HELPER FUNCTIONS FOR EASY INTEGRATION
export const logUserDataAccess = (userId: string, action: string, resourceType: string, resourceId?: string) => {
  auditLogger.logDataAccess({
    userId,
    action,
    resourceType,
    resourceId,
    sensitiveDataAccessed: true,
  });
};

export const logPublicDataAccess = (action: string, resourceType: string, resourceId?: string) => {
  auditLogger.logDataAccess({
    action,
    resourceType,
    resourceId,
    sensitiveDataAccessed: false,
  });
};

export const monitorDataAccessPerformance = async <T>(
  operation: () => Promise<T>,
  context: {
    action: string;
    resourceType: string;
    userId?: string;
    resourceId?: string;
  }
): Promise<T> => {
  return auditLogger.monitorPerformance(operation, context);
};
